/**
 * Shared percentage formatting utility for consistent display across the app.
 *
 * Takes a decimal value (0-1) and returns a formatted percentage string WITHOUT the % sign.
 * Examples: 0.5 → "50", 0.3333 → "33.33", 0.9999999 → "100", 0.99999 → "99.99"
 *
 * Key rules:
 * - Values >= 0.9999999 are treated as truly 100% (floating point artifact threshold)
 * - Values that would round to 100% but are below the threshold are capped at "99.99"
 * - Whole numbers display without decimals (e.g., "50" not "50.00")
 * - Up to 2 decimal places shown when needed
 */
export function formatPercentage(value: number): string {
  if (!value || value === 0) return '';

  // Threshold: 7 nines after decimal = floating point artifact of 100%
  const isActuallyComplete = value >= 0.9999999;
  const percentValue = value * 100;

  // If actually complete, show 100
  if (isActuallyComplete) {
    return '100';
  }

  // If it would round to 100 but isn't actually complete, cap at 99.99
  if (percentValue >= 99.995) {
    return '99.99';
  }

  // Round to 2 decimal places
  const rounded = Math.round(percentValue * 100) / 100;

  // If it's a whole number, show without decimals
  if (rounded % 1 === 0) {
    return `${Math.round(rounded)}`;
  }

  // Check if only 1 decimal place is needed
  const roundedToOne = Math.round(percentValue * 10) / 10;
  if (Math.abs(rounded - roundedToOne) < 0.001) {
    return roundedToOne.toFixed(1);
  }

  // Otherwise show with 2 decimals
  return rounded.toFixed(2);
}

/**
 * Same as formatPercentage but appends the % sign.
 * Used in PDF templates and other display contexts.
 */
export function formatPercentageWithSign(value: number): string {
  const formatted = formatPercentage(value);
  return formatted ? `${formatted}%` : '';
}

/**
 * Rounds a currency value to 2 decimal places to avoid floating point issues.
 * Use this whenever working with billedToDate, storedToDate, or any dollar amount
 * that comes from the API with floating point imprecision.
 */
export function roundCurrency(value: number): number {
  return Math.round(value * 100) / 100;
}
